import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Switch,
    Typography
} from "@mui/material";
import Grid from '@mui/material/Grid';
import * as moment from 'moment-timezone';
import React, {useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {useMutation, useQuery} from "@apollo/client";

import {
    ALL_SERVICE_TYPES,
    MUTATION_CREATE_SHIFT,
    MUTATION_UPDATE_SHIFT,
} from "../queries/GraphQLQueries";
import ServiceTypeMultiSelect from "./common/ServiceTypeMultiSelect";
import CustomAlert from "./CustomAlert";
import {DATE_WITH_TIME_FORMAT} from "../config/DateTimeConstants";
import {useDialogNavigation} from "../hooks/useDialogNavigation";
import {
    ROUNDING_MINUTES_FOR_SHIFT_END_TIME,
    SECONDS_INTO_MINUTES,
    MINUTES,
    WARNING_UPDATE_DELAY_IN_MILISECONDS
} from "./shift-plans/AddShiftPlanDialog";
import {BOOKABLE_STATUS} from "../domain/BookableStatus";
import {
    hasDifferentKeycodes,
    hasDifferentBookableStatus,
    hasDifferentTimestamps,
    isTimeInvalid, determineShiftQueryName,
    isSameServiceTypesNotOverlapping
} from "../helpers/ShiftHelper";
import ShiftTimeSelection from "./schedule/ShiftTimeSelection";

export default function CreateEditShiftDialog() {

    const serviceTypeSelectionOptions = {
        enableBookableServices: true,
        enableServiceTypeDetails: false
    };

    const handleCancel = useDialogNavigation();
    const {state} = useLocation();
    const {shiftInfo, userName, userId, chosenStartTime, isEditMode, currentViewType} = state || {};

    const DEFAULT_SHIFT_START_TIME = chosenStartTime || moment(shiftInfo?.shiftStartTime).add(shiftInfo?.checkInMinutes, MINUTES);
    const DEFAULT_SHIFT_END_TIME = shiftInfo?.shiftEndTime || moment(chosenStartTime).add(ROUNDING_MINUTES_FOR_SHIFT_END_TIME, MINUTES);
    const DEFAULT_INCLUDED_CHECK_IN_TIME = shiftInfo?.checkInTimeIncluded || true;

    const [shiftStartTime, setShiftStartTime] = useState(DEFAULT_SHIFT_START_TIME);
    const [shiftEndTime, setShiftEndTime] = useState(DEFAULT_SHIFT_END_TIME);
    const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
    const [canSubmit, setCanSubmit] = useState(false);
    const [allServiceTypes, setAllServiceTypes] = useState([]);
    const [includeCheckInTimeslot, setIncludeCheckInTimeslot] = useState(DEFAULT_INCLUDED_CHECK_IN_TIME);
    const [alertMessage, setAlertMessage] = useState(null);
    const [bookableStatuses, setBookableStatuses] = useState({});
    const [bookableServiceTypesTimestamps, setBookableServiceTypesTimestamps] = useState({});
    const [showWarning, setShowWarning] = useState(false);

    const handleCloseCustomAlert = () => setAlertMessage(null);
    const handleBookableServiceTypesTimestampUpdate = (newTimeStamps) => setBookableServiceTypesTimestamps(newTimeStamps);

    const { loading, errors, data: serviceData } = useQuery(ALL_SERVICE_TYPES);
    const refetchShiftQueriesForView = {refetchQueries: [determineShiftQueryName(currentViewType)]};
    
    const [updateShift] = useMutation(MUTATION_UPDATE_SHIFT, refetchShiftQueriesForView);
    const [createShift] = useMutation(MUTATION_CREATE_SHIFT, refetchShiftQueriesForView);

    useEffect(() => {
        const canEnableSubmit =
            (moment(shiftStartTime).format(DATE_WITH_TIME_FORMAT).trim() !== ''
            && moment(shiftStartTime).isValid()
            && moment(shiftEndTime).format(DATE_WITH_TIME_FORMAT).trim() !== ''
            && moment(shiftEndTime).isValid()
            && !isTimeInvalid(bookableServiceTypesTimestamps, shiftStartTime, shiftEndTime)
            && isSameServiceTypesNotOverlapping(bookableServiceTypesTimestamps)
            && selectedServiceTypes.length > 0)
            && (!moment(shiftStartTime).isSame(DEFAULT_SHIFT_START_TIME)
            || !moment(shiftEndTime).isSame(shiftInfo?.shiftEndTime)
            || includeCheckInTimeslot !== shiftInfo?.checkInTimeIncluded
            || selectedServiceTypes.length !== shiftInfo?.serviceType.length
            || hasDifferentKeycodes(selectedServiceTypes, shiftInfo)
            || hasDifferentBookableStatus(bookableStatuses, shiftInfo)
            || hasDifferentTimestamps(selectedServiceTypes, shiftInfo, bookableServiceTypesTimestamps));

        setCanSubmit(canEnableSubmit);
    }, [
        shiftStartTime,
        shiftEndTime,
        selectedServiceTypes,
        includeCheckInTimeslot,
        bookableStatuses,
        bookableServiceTypesTimestamps
    ]);

    useEffect(() => {
        initializeDefaultStateValues();

        if (!serviceData?.serviceTypes) {
            return;
        }
    
        const allTypes = serviceData.serviceTypes;
        setAllServiceTypes(allTypes);
    
        if (isEditMode && shiftInfo?.serviceType?.length > 0) {
            handleEditMode(allTypes, shiftInfo);
        } else {
            resetServiceSelections();
        }
    }, [serviceData, shiftInfo, isEditMode]);
    
    const initializeDefaultStateValues = () => {
        setShiftStartTime(DEFAULT_SHIFT_START_TIME);
        setShiftEndTime(DEFAULT_SHIFT_END_TIME);
        setIncludeCheckInTimeslot(DEFAULT_INCLUDED_CHECK_IN_TIME);
    };
    
    const handleEditMode = (allTypes, shiftInfo) => {
        const defaultSelectedServiceTypes = getDefaultSelectedServiceTypes(allTypes, shiftInfo);
        setSelectedServiceTypes(defaultSelectedServiceTypes);
    
        const initialBookableStatuses = getInitialBookableStatuses(defaultSelectedServiceTypes, shiftInfo);
        const initialTimestamps = getInitialTimestamps(defaultSelectedServiceTypes, shiftInfo);
    
        setBookableStatuses(initialBookableStatuses);
        setBookableServiceTypesTimestamps(initialTimestamps);
    };
    
    const getDefaultSelectedServiceTypes = (allTypes, shiftInfo) => {
        return shiftInfo.serviceType
            .map(shiftServiceType => {
                const matchingType = allTypes.find(type => type.keycode === shiftServiceType.keycode);
                if (!matchingType) {
                    return null;
                }
    
                return {
                    ...matchingType,
                    id: shiftServiceType.id,
                    uniqueBookableServiceRowId: `${shiftServiceType.keycode}-${shiftServiceType.id}`
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.id - b.id);
    };
    
    const getInitialBookableStatuses = (selectedServiceTypes, shiftInfo) => {
        return selectedServiceTypes.reduce((accreditation, serviceType) => {
            const matchingService = shiftInfo.serviceType.find(s => s.id === serviceType.id);
            accreditation[serviceType.uniqueBookableServiceRowId] = matchingService?.isBookable
                ? BOOKABLE_STATUS.Bookable
                : BOOKABLE_STATUS.Unbookable;
            return accreditation;
        }, {});
    };
    
    const getInitialTimestamps = (selectedServiceTypes, shiftInfo) => {
        return selectedServiceTypes.reduce((accreditation, serviceType) => {
            const matchingService = shiftInfo.serviceType.find(s => s.id === serviceType.id);
            accreditation[serviceType.uniqueBookableServiceRowId] = {
                startTime: matchingService?.startTime,
                endTime: matchingService?.endTime
            };
            return accreditation;
        }, {});
    };
    
    const resetServiceSelections = () => {
        setSelectedServiceTypes([]);
        setBookableStatuses({});
        setBookableServiceTypesTimestamps({});
    };  

    useEffect(() => {
        if (selectedServiceTypes.length === 0) {
            return;
        }
    
        const updatedServiceTimes = { ...bookableServiceTypesTimestamps };
    
        selectedServiceTypes.forEach(serviceType => {
            const updatedTime = calculateUpdatedServiceTime(serviceType, updatedServiceTimes);
            if (updatedTime) {
                updatedServiceTimes[serviceType.uniqueBookableServiceRowId] = updatedTime;
            }
        });
    
        setBookableServiceTypesTimestamps(updatedServiceTimes);
    }, [shiftStartTime, shiftEndTime, selectedServiceTypes]);
    
    const shouldUpdateServiceTime = (existingTime) => {
        if (!existingTime) {
            return true;
        }

        const isInPast = moment(existingTime.startTime).isBefore(moment(shiftStartTime));
        const isInFuture = moment(existingTime.endTime).isAfter(moment(shiftEndTime));

        return isInPast || isInFuture;
    };
    
    const calculateUpdatedServiceTime = (serviceType, updatedServiceTimes) => {
        const existingTime = updatedServiceTimes[serviceType.uniqueBookableServiceRowId];
    
        if (!shouldUpdateServiceTime(existingTime)) {
            return null;
        }
    
        const shiftStart = moment(shiftStartTime);
        const shiftEnd = moment(shiftEndTime);
        const maxDurationInMinutes = (serviceType.timeslotLengthInSeconds * serviceType.maxPeriodsInShift) / SECONDS_INTO_MINUTES;
        const maxEndTime = shiftStart.clone().add(maxDurationInMinutes, 'minutes');
        const adjustedEndTime = shiftEnd.isAfter(maxEndTime) ? maxEndTime : shiftEnd;
    
        const startTime = existingTime && adjustedEndTime.isSameOrBefore(existingTime.startTime)
            ? shiftStart
            : existingTime?.startTime || shiftStart;
    
        return {
            id: serviceType.id,
            startTime,
            endTime: adjustedEndTime
        };
    };
    

    useEffect(() => {
        const timer = setTimeout(() => {
          if (selectedServiceTypes.length === 0) {
            setShowWarning(false);
          } else {
            setShowWarning(
                isTimeInvalid(bookableServiceTypesTimestamps, shiftStartTime, shiftEndTime)|| 
                !isSameServiceTypesNotOverlapping(bookableServiceTypesTimestamps)
            );
          }
        }, WARNING_UPDATE_DELAY_IN_MILISECONDS);

        return () => clearTimeout(timer);
    }, [selectedServiceTypes, bookableServiceTypesTimestamps, shiftStartTime, shiftEndTime]);

    const handleBookableStatusChange = (serviceType, uniqueBookableServiceRowId, status) => {
        setBookableStatuses(prevStatuses => {

            const newState = {
                ...prevStatuses,
                [uniqueBookableServiceRowId]: status
            }

            return newState;
        });
    };

    const handleUpdateShift = () => {
        setAlertMessage({children: 'Updating shift...', severity: 'success'});
        setCanSubmit(false);

        updateShift(
          {
              variables: {
                  shiftId: shiftInfo?.availabilityId,
                  updateBookableServiceTypesInput: buildBookableServiceTypesPlanInput(),
                  from: moment(shiftStartTime).format(DATE_WITH_TIME_FORMAT),
                  to: moment(shiftEndTime).format(DATE_WITH_TIME_FORMAT),
                  includeCheckInTimeslot: includeCheckInTimeslot
              }
          })
        .then(() => handleCancel())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            setCanSubmit(true);
            console.log(error);
        });
    }

    const handleCreateShift = () => {
        setAlertMessage({children: 'Creating shift...', severity: 'success'});
        setCanSubmit(false);

        const serviceTypesPlanInput = buildBookableServiceTypesPlanInput()

        createShift(
          {
              variables: {
                  ownerId: userId,
                  createBookableServiceTypesInput: serviceTypesPlanInput,
                  from: moment(shiftStartTime).format(DATE_WITH_TIME_FORMAT),
                  to: moment(shiftEndTime).format(DATE_WITH_TIME_FORMAT),
                  includeCheckInTimeslot: includeCheckInTimeslot
              }
          })
        .then(() => handleCancel())
        .catch(error => {
            setAlertMessage({children: error?.message, severity: 'error'});
            setCanSubmit(true);
            console.log(error);
        });
    }

    const buildBookableServiceTypesPlanInput = () =>
      selectedServiceTypes
      .map(serviceType => {
          const uniqueBookableServiceRowId = serviceType.uniqueBookableServiceRowId;
          const serviceTypeKeycode = serviceType.keycode;
          const isBookable = bookableStatuses[uniqueBookableServiceRowId] !== undefined
            ? bookableStatuses[uniqueBookableServiceRowId] === BOOKABLE_STATUS.Bookable
            : true;

          const serviceTypesTimestamp = bookableServiceTypesTimestamps[uniqueBookableServiceRowId] || {};
          const {id, startTime, endTime} = serviceTypesTimestamp;

          return {
              serviceTypeKeycode,
              id,
              startTime: moment(startTime).format(DATE_WITH_TIME_FORMAT),
              endTime: moment(endTime).format(DATE_WITH_TIME_FORMAT),
              isBookable
          }
      });

    const handleCheckInTimeSlotChange = (event) => setIncludeCheckInTimeslot(event.target.checked);

    return <>
        <Dialog open={true} onClose={handleCancel} maxWidth={false}
            sx={{
                '& .MuiDialog-paper': {
                    width: '43.75rem',
                    maxWidth: 'auto',
                },
            }}>
            <DialogTitle>
                {
                    isEditMode
                      ? `Edit Shift for ${shiftInfo?.displayName || userName}`
                      : `Create Shift for ${shiftInfo?.displayName || userName}`
                }
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <ShiftTimeSelection 
                        shiftStartTime={shiftStartTime}
                        setShiftStartTime={setShiftStartTime}
                        shiftEndTime={shiftEndTime}
                        setShiftEndTime={setShiftEndTime}
                    />
                    <Grid container spacing={0}>
                        <Grid size={12}>
                            <Typography variant="h6">Shift Bookable Services</Typography>
                        </Grid>

                        <Grid size={12}>
                            <ServiceTypeMultiSelect
                              selectedServiceTypes={selectedServiceTypes}
                              setSelectedServiceTypes={setSelectedServiceTypes}
                              availableServiceTypes={allServiceTypes}
                              bookableServiceTypes={shiftInfo?.serviceType}
                              serviceTypeSelectionOptions={serviceTypeSelectionOptions}
                              handleBookableStatusChange={handleBookableStatusChange}
                              setBookableStatuses={setBookableStatuses}
                              handleBookableServiceTypesTimestampUpdate={handleBookableServiceTypesTimestampUpdate}
                              bookableServiceTypesTimestamps={bookableServiceTypesTimestamps}
                              bookableStatuses={bookableStatuses}
                              startTime={shiftStartTime}
                              endTime={shiftEndTime}/>
                        </Grid>
                    </Grid>
                    <Grid size={12}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2">Require Performer Check-in</Typography>
                            <Switch checked={includeCheckInTimeslot} onChange={handleCheckInTimeSlotChange} />
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
            <Grid sx={{marginRight: "auto", marginLeft: "1rem"}}>
                {showWarning && (
                    <Typography color="error" sx={{ fontWeight: 'bold' }}>
                        Warning: The shift and bookable service<br />times don't match up.
                    </Typography>
                )}
            </Grid>
                <Button variant="contained" onClick={handleCancel}>Cancel</Button>
                <Button variant="contained" onClick={isEditMode ? handleUpdateShift : handleCreateShift} disabled={!canSubmit} sx={{marginRight: "1rem"}}>
                    {isEditMode ? "Update Shift" : "Create Shift"}
                </Button>
            </DialogActions>
        </Dialog>
        <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />
    </>
}